import { usePortFetch } from '@ais-ui/api';
import { useEffect, useRef, useState } from 'react';
import { Port } from '../Map/AISMapContext';
import type { PortExtended } from '@ais-ui/api-spec/portserver';

import { shapeToVectorLayer, useLayers } from '@ais-ui/react-openlayers';
import { GeoJson } from '@ais-ui/api-spec/shapes';

const createPortQuayFeatureCollection = (
  portDetails: PortExtended
): GeoJson => {
  const features: Array<GeoJson> = [];

  portDetails.quay?.forEach((quay) => {
    // Add quay line
    if (quay.line) {
      features.push({
        type: 'Feature',
        geometry: quay.line,
        properties: {
          featureType: 'quay',
          id: quay?.id ? quay.id.toString() : 'Unknown ID',
          name: quay.name,
          data: quay.data,
        },
      });
    }

    // Add quay polygon
    if (quay.polygon) {
      features.push({
        type: 'Feature',
        geometry: quay.polygon,
        properties: {
          featureType: 'quay',
          id: quay?.id ? quay.id.toString() : 'Unknown ID',
          name: quay.name,
          data: quay.data,
        },
      });
    }
  });

  return {
    features: features,
    properties: {},
    type: 'FeatureCollection',
  };
};

const createPortBollardFeatureCollection = (
  portDetails: PortExtended
): GeoJson => {
  const features: Array<GeoJson> = [];

  portDetails.quay?.forEach((quay) => {
    quay.bollards?.forEach((bollard) => {
      features.push({
        type: 'Feature',
        geometry: bollard.position,
        properties: {
          featureType: 'bollard',
          id: bollard?.id ? bollard.id.toString() : 'Unknown ID',
          quayId: quay.id,
        },
      });
    });
  });

  return {
    features: features,
    properties: {},
    type: 'FeatureCollection',
  };
};

export const useDrawPort = (currentPort: Port | null) => {
  const portFetch = usePortFetch();
  const { addLayer, doesLayerExist } = useLayers();
  const [portDetails, setPortDetails] = useState<PortExtended | null>(null);
  const quayLayerCreatedRef = useRef(false);
  const bollardLayerCreatedRef = useRef(false);
  const portFetchFailedRef = useRef(false);
  const lastPortIdRef = useRef<string | number | null>(null);

  useEffect(() => {
    const fetchPortDetails = async () => {
      if (!currentPort) {
        setPortDetails(null);
        lastPortIdRef.current = null;
        return;
      }

      // Reset refs when port changes
      if (lastPortIdRef.current !== currentPort.id) {
        quayLayerCreatedRef.current = false;
        bollardLayerCreatedRef.current = false;
        portFetchFailedRef.current = false;
        lastPortIdRef.current = currentPort.id;
      }

      // Don't fetch if we've already failed for this specific port
      if (portFetchFailedRef.current) {
        return;
      }

      try {
        const details = await portFetch(currentPort.id);
        setPortDetails(details);
      } catch (error) {
        console.error('Error fetching port details:', error);
        setPortDetails(null);
        portFetchFailedRef.current = true;
      }
    };

    fetchPortDetails();
  }, [currentPort?.id, portFetch]); // Only depend on currentPort.id, not the whole object

  // Effect for creating quay layer
  useEffect(() => {
    if (!portDetails) {
      return;
    }
    const layerName = `${portDetails.name} quay`;

    if (quayLayerCreatedRef.current || doesLayerExist(layerName)) {
      return;
    }

    const quay = createPortQuayFeatureCollection(portDetails);

    if (quay?.features?.length === 0) {
      quayLayerCreatedRef.current = true;
      return;
    }

    const quayLayer = shapeToVectorLayer(quay);

    addLayer(
      quayLayer,
      layerName,
      true,
      true,
      true,
      {},
      layerName,
      false,
      false,
      true
    );

    quayLayerCreatedRef.current = true;
  }, [portDetails, addLayer, doesLayerExist]);

  // Effect for creating bollard layer
  useEffect(() => {
    if (!portDetails) {
      return;
    }
    const layerName = `${portDetails.name} bollards`;

    if (bollardLayerCreatedRef.current || doesLayerExist(layerName)) {
      return;
    }

    const bollards = createPortBollardFeatureCollection(portDetails);

    if (bollards?.features?.length === 0) {
      bollardLayerCreatedRef.current = true;
      return;
    }

    const bollardsLayer = shapeToVectorLayer(bollards);

    addLayer(
      bollardsLayer,
      layerName,
      true,
      true,
      false,
      {},
      layerName,
      false,
      false,
      true
    );

    bollardLayerCreatedRef.current = true;
  }, [portDetails, addLayer, doesLayerExist]);

  return portDetails;
};
